interface ResumeCertification {
  name: string;
  issuer: string;
  date: string;
}

export const RESUME_DATA = {
  personalInfo: {
    name: "Ahmed Hamid",
    title: "Full Stack Developer | Frontend Developer | Wix App Developer",
    email: "ahmedhamid8250@gmail.com",
    phone: "+92-323-3591658",
    location: "Lahore, Pakistan",
    linkedin: "linkedin.com/in/ahmadhamid03",
    github: "github.com/ahmed-dev8250",
    portfolio: "ahmed-dev8250.vercel.app",
  },

  summary: `Self-taught Full Stack Developer focused on React, TypeScript, Next.js, Node.js, and Wix App development. Started learning web development in 2023 and began contributing to real production projects in 2024. Experienced in building Wix App Market products, frontend interfaces, dashboards, integrations, and full-stack web experiences for real users.`,

  education: [
    {
      degree: "Self-taught Web Development",
      institution: "Independent learning, real projects, and professional work",
      location: "Lahore, Pakistan",
      duration: "2023 - Present",
      gpa: "Not applicable",
    },
    {
      degree: "Formal Education",
      institution: "Completed studies up to Grade 6",
      location: "Pakistan",
      duration: "Completed",
      gpa: "Not applicable",
    },
  ],

  workExperience: [
    {
      position: "Full Stack / Wix App Developer",
      company: "Ichonic.",
      location: "Lahore, Pakistan / Remote",
      duration: "2024 - Present",
      responsibilities: [
        "Developed and maintained production apps for the Wix App Market using React, TypeScript, Wix Blocks, and modern frontend tooling.",
        "Built user-facing interfaces, dashboard flows, settings panels, and app experiences for Wix site owners.",
        "Worked on multiple Ichonic Wix apps listed on the Wix App Market, plus additional apps submitted for Wix review.",
        "Integrated frontend features with backend services, third-party APIs, authentication flows, and app configuration systems.",
        "Collaborated with designers and developers to convert requirements into clean, responsive, production-ready UI.",
      ],
      technologies: [
        "React",
        "TypeScript",
        "Next.js",
        "Node.js",
        "MongoDB",
        "Wix Blocks",
        "Wix App Development",
        "Tailwind CSS",
        "Material UI",
        "Framer Motion",
        "Redux",
      ],
    },
  ],

  technicalSkills: {
    frontend: [
      "JavaScript",
      "TypeScript",
      "React.js",
      "Next.js",
      "Redux",
      "HTML5",
      "CSS3",
      "Tailwind CSS",
      "Material UI",
      "Shadcn UI",
      "Bootstrap",
      "Framer Motion",
    ],
    backend: ["Node.js", "Express.js", "REST APIs", "GraphQL"],
    databases: ["MongoDB", "PostgreSQL", "NeonDB", "Supabase", "Firebase"],
    tools: [
      "Git",
      "GitHub",
      "Vercel",
      "Netlify",
      "Figma",
      "Postman",
      "Wix App Development",
      "Wix Blocks",
    ],
    ai: ["N8N", "Zapier", "Make", "AI Integrations", "Google Gemini API"],
  },

  projects: [
    {
      name: "Chattify",
      description:
        "Personal Wix App Market submission focused on helping Wix site owners add quick communication/contact experiences to their websites.",
      technologies: ["Wix App", "React", "TypeScript", "Wix Blocks"],
      highlights: [
        "Built the app experience for Wix site owners and website visitors.",
        "Prepared app listing, privacy policy, and submission flow for Wix review.",
        "Focused on a simple setup experience and clean user-facing interface.",
      ],
      liveUrl:
        "https://www.wix.com/app-market/test/3c5e60db-c1b4-4304-bd7e-4b6feb44d568",
      githubUrl: "",
    },
    {
      name: "Ichonic Wix App Market Apps",
      description:
        "Multiple production Wix apps developed with Ichonic. for the Wix App Market.",
      technologies: ["React", "TypeScript", "Wix Blocks", "Wix APIs"],
      highlights: [
        "Worked on app UI, settings flows, frontend architecture, and integration details.",
        "Contributed to apps already listed on the Wix App Market.",
        "Supported additional submitted apps that are currently awaiting public release.",
      ],
      liveUrl: "https://www.wix.com/app-market/search-result?query=ichonic",
      githubUrl: "",
    },
    {
      name: "Developer Portfolio",
      description:
        "Personal portfolio built to showcase experience, Wix app work, skills, and project case studies.",
      technologies: ["React", "TypeScript", "Tailwind CSS", "Vite"],
      highlights: [
        "Built responsive sections for work, skills, about, contact, and privacy policy.",
        "Added a private project dashboard backed by MongoDB.",
        "Designed for future updates without changing frontend project code.",
      ],
      liveUrl: "https://ahmed-dev8250.vercel.app",
      githubUrl: "https://github.com/ahmed-dev8250",
    },
  ],

  certifications: [] as ResumeCertification[],

  languages: [
    { name: "Urdu", proficiency: "Native" },
    { name: "Punjabi", proficiency: "Native" },
    { name: "English", proficiency: "Basic conversational" },
  ],

  achievements: [
    "Started self-learning web development in 2023 and moved into real production work in 2024.",
    "Worked on multiple Wix App Market products for Ichonic.",
    "Submitted a personal Wix app, Chattify, for Wix App Market review.",
  ],
};

export const getFormattedResumeText = (): string => {
  const {
    personalInfo,
    summary,
    education,
    workExperience,
    technicalSkills,
    projects,
    certifications,
    languages,
    achievements,
  } = RESUME_DATA;

  return `
PERSONAL INFORMATION:
Name: ${personalInfo.name}
Title: ${personalInfo.title}
Email: ${personalInfo.email}
Phone: ${personalInfo.phone}
Location: ${personalInfo.location}
LinkedIn: ${personalInfo.linkedin}
GitHub: ${personalInfo.github}
Portfolio: ${personalInfo.portfolio}

PROFESSIONAL SUMMARY:
${summary}

EDUCATION:
${education
  .map(
    (edu) => `
- ${edu.degree} at ${edu.institution} (${edu.duration})
  Location: ${edu.location}
  GPA: ${edu.gpa}
`,
  )
  .join("\n")}

WORK EXPERIENCE:
${workExperience
  .map(
    (work) => `
${work.position} at ${work.company}
Duration: ${work.duration} | Location: ${work.location}
Key Responsibilities:
${work.responsibilities.map((r) => `  - ${r}`).join("\n")}
Technologies: ${work.technologies.join(", ")}
`,
  )
  .join("\n")}

TECHNICAL SKILLS:
Frontend: ${technicalSkills.frontend.join(", ")}
Backend: ${technicalSkills.backend.join(", ")}
Databases: ${technicalSkills.databases.join(", ")}
Tools & Platforms: ${technicalSkills.tools.join(", ")}
AI & Automation: ${technicalSkills.ai.join(", ")}

PROJECTS:
${projects
  .map(
    (project) => `
${project.name}
${project.description}
Technologies: ${project.technologies.join(", ")}
Key Features:
${project.highlights.map((h) => `  - ${h}`).join("\n")}
Links: Live: ${project.liveUrl} | GitHub: ${project.githubUrl}
`,
  )
  .join("\n")}

CERTIFICATIONS:
${
  certifications.length
    ? certifications
        .map((cert) => `- ${cert.name} by ${cert.issuer} (${cert.date})`)
        .join("\n")
    : "- No formal certifications listed"
}

LANGUAGES:
${languages.map((lang) => `- ${lang.name}: ${lang.proficiency}`).join("\n")}

ACHIEVEMENTS:
${achievements.map((achievement) => `- ${achievement}`).join("\n")}
`;
};
